import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnInit,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormGroup,
  FormBuilder,
  Validators,
  FormArray,
  ReactiveFormsModule,
} from '@angular/forms';
import { ScreenType } from 'src/app/enums/enums';
import { NavigationService } from 'src/app/services/navigation.service';
import { ShipmentFormData } from 'src/app/models/shipment/shipment-form-data.model';
import { ParcelData } from 'src/app/models/shipment/parcel-data.model';
import { FormService } from 'src/app/services/form.service';
import { ErrorMsg } from 'src/app/models/error/error-msg.model';
import { ErrorResponse } from 'src/app/models/error/error-response.model';
import { SubmitButtonComponent } from '../../../common/submit-button/submit-button.component';
import { BackButtonComponent } from '../../../common/back-button/back-button.component';
import { FormInputControlComponent } from '../../../common/form-input-control/form-input-control.component';
import {
  accountNumberValidator,
  codAmountValidator,
  customMaxValidator,
  customMinValidator,
  incAmountValidator,
  incMinValidator,
  parcelContentValidator,
  parcelNotStandardCheckboxValidator,
  sumOfFieldsValidator,
  sumParcelCountValidator,
} from 'src/app/common/validatos/validators';
import { FormErrorMessageComponent } from '../../../common/form-error-message/form-error-message.component';
import { StoreService } from 'src/app/services/store.service';

@Component({
  selector: 'app-parcel-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SubmitButtonComponent,
    BackButtonComponent,
    FormInputControlComponent,
    FormErrorMessageComponent,
  ],
  templateUrl: './parcel-form.component.html',
  styleUrls: ['./parcel-form.component.scss'],
})
export class ParcelFormComponent implements OnInit, AfterViewInit {
  ScreenType = ScreenType;

  dynamicForm!: FormGroup;
  submitException = signal(false);
  isSubmitLoading = signal(false);

  constructor(
    private navigationService: NavigationService,
    private fb: FormBuilder,
    public formService: FormService,
    private cdr: ChangeDetectorRef,
    private storeService: StoreService
  ) {
    this.initializeForm();
    this.subscribeToFormChanges();
  }

  ngOnInit() {
    this.syncCheckboxes();
  }

  ngAfterViewInit() {
    this.handleFormErrors();
  }

  get parcelIsCod() {
    return this.dynamicForm.get('parcel_is_cod');
  }

  get parcelCodAmount() {
    return this.dynamicForm.get('parcel_cod_amount');
  }

  get parcelAccountNumber() {
    return this.dynamicForm.get('parcel_account_number');
  }

  get parcelIsInsurance() {
    return this.dynamicForm.get('parcel_is_insurance');
  }

  get parcelInsuranceAmount() {
    return this.dynamicForm.get('parcel_insurance_amount');
  }

  get parcelContent() {
    return this.dynamicForm.get('parcel_content');
  }

  get rows(): FormArray {
    return this.dynamicForm.get('rows') as FormArray;
  }

  private initializeForm() {
    const formData = this.storeService.getShipmentFormData();
    this.dynamicForm = this.fb.group(
      {
        parcel_is_cod: [formData?.isCod],
        parcel_cod_amount: [{ value: formData?.codAmount, disabled: true }],
        parcel_account_number: [
          { value: formData?.accountNumber, disabled: true },
        ],
        parcel_is_insurance: [formData?.isIns],
        parcel_insurance_amount: [
          { value: formData?.insAmount, disabled: true },
        ],
        parcel_content: [
          { value: formData?.insShipmentContentDescription, disabled: true },
        ],
        rows: this.fb.array(this.createRows(formData?.parcels)),
      },
      {
        validators: [
          codAmountValidator('parcel_is_cod', 'parcel_cod_amount'),
          accountNumberValidator('parcel_is_cod', 'parcel_account_number'),
          incAmountValidator(
            'parcel_is_cod',
            'parcel_cod_amount',
            'parcel_is_insurance',
            'parcel_insurance_amount'
          ),
          parcelContentValidator('parcel_is_insurance', 'parcel_content'),
          incMinValidator(
            'parcel_is_cod',
            'parcel_cod_amount',
            'parcel_insurance_amount'
          ),
          sumParcelCountValidator('rows', 'parcel_count', 20),
        ],
      }
    );
  }

  private subscribeToFormChanges() {
    this.dynamicForm.valueChanges.subscribe(() => {
      this.saveFormDataToCache();
    });
  }

  private syncCheckboxes() {
    this.parcelIsCod?.valueChanges.subscribe((value) => {
      this.parcelIsInsurance?.setValue(value, { emitEvent: false });
    });
    this.parcelIsInsurance?.valueChanges.subscribe((value) => {
      if (this.parcelIsCod?.value === true) {
        this.parcelIsCod?.setValue(value, { emitEvent: false });
      }
    });
  }

  saveFormDataToCache(): void {
    const parcels: ParcelData[] = this.rows.controls.map((row) => {
      const rowGroup = row as FormGroup;
      return {
        count: rowGroup.get('parcel_count')?.value,
        weight: rowGroup.get('parcel_weight')?.value,
        length: rowGroup.get('parcel_length')?.value,
        width: rowGroup.get('parcel_width')?.value,
        height: rowGroup.get('parcel_height')?.value,
        isNotStandard: rowGroup.get('parcel_not_standard')?.value,
      };
    });

    const shipmentFormData: ShipmentFormData = {
      isCod: this.parcelIsCod?.value,
      codAmount: this.parcelCodAmount?.value,
      accountNumber: this.parcelAccountNumber?.value,
      isIns: this.parcelIsInsurance?.value,
      insAmount: this.parcelInsuranceAmount?.value,
      insShipmentContentDescription: this.parcelContent?.value,
      parcels,
      palettes: undefined,
    };

    this.storeService.setShipmentFormData(shipmentFormData);
  }

  createRows(parcels?: ParcelData[]): FormGroup[] {
    return (
      parcels?.map((parcel) => this.createRow(parcel)) || [this.createRow()]
    );
  }

  createRow(parcel?: ParcelData): FormGroup {
    return this.fb.group(
      {
        parcel_count: [
          parcel?.count,
          [Validators.required, customMinValidator(1, 'min_parcel_count')],
        ],
        parcel_weight: [
          parcel?.weight,
          [
            Validators.required,
            customMaxValidator(70, 'max_parcel_weight'),
            customMinValidator(1, 'min_parcel_weight'),
          ],
        ],
        parcel_length: [
          parcel?.length,
          [
            Validators.required,
            customMaxValidator(200, 'max_parcel_length'),
            customMinValidator(1, 'min_parcel_length'),
          ],
        ],
        parcel_width: [
          parcel?.width,
          [
            Validators.required,
            customMaxValidator(200, 'max_parcel_width'),
            customMinValidator(1, 'min_parcel_width'),
          ],
        ],
        parcel_height: [
          parcel?.height,
          [
            Validators.required,
            customMaxValidator(200, 'max_parcel_height'),
            customMinValidator(1, 'min_parcel_height'),
          ],
        ],
        parcel_not_standard: [parcel?.isNotStandard],
      },
      {
        validators: [
          sumOfFieldsValidator(
            ['parcel_length', 'parcel_width', 'parcel_height'],
            300,
            'sum_of_parcel_dimensions_exceeded'
          ),
          // parcelNotStandardCheckboxValidator(
          //   'parcel_height',
          //   'parcel_length',
          //   'parcel_width',
          //   'parcel_not_standard'
          // ),
        ],
      }
    );
  }

  private handleFormErrors() {
    const errorResponse = this.storeService.getErrorResponse();
    let errorMsgCopy: ErrorMsg[] | undefined = undefined;

    if (errorResponse) {
      errorMsgCopy = errorResponse?.errorMsg?.filter((err) => {
        let control: any;
        const rowArray = this.rows.at(err.idRow) as FormGroup;

        switch (err.field) {
          case 'items': {
            if (err.idRow == null) {
              control = this.rows.at(this.rows.length - 1).get('parcel_count');
            } else {
              control = control.get('parcel_count');
            }
            break;
          }
          case 'items.quantity': {
            control = rowArray.get('parcel_count');
            break;
          }
          case 'item.length': {
            control = rowArray.get('parcel_length');
            break;
          }
          case 'item.height': {
            control = rowArray.get('parcel_height');
            break;
          }
          case 'item.compWeight': {
            control = rowArray.get('parcel_weight');
            break;
          }
          case 'item.width+item.length': {
            const control1 = rowArray.get('parcel_width');
            const control2 = rowArray.get('parcel_length');
            if (control1) control1.setErrors({ [err.errorCode]: true });
            if (control2) control2.setErrors({ [err.errorCode]: true });
            return false;
          }
          case 'INS_VALUE': {
            control = this.parcelInsuranceAmount;
            break;
          }
          case 'INS_PCK_CONTENT': {
            control = this.parcelContent;
            break;
          }
          case 'COD_VALUE': {
            control = this.parcelCodAmount;
            break;
          }
          case 'COD_ACCOUNT': {
            control = this.parcelAccountNumber;
            break;
          }
        }

        if (control) {
          control.setErrors({ [err.errorCode]: true });
          control.markAsTouched();
          return false;
        }

        return true;
      });
    }

    const errorRespCpy: ErrorResponse = {
      httpStatus: errorResponse?.httpStatus,
      errorMsg: errorMsgCopy,
    };

    this.storeService.setPickupErrorResponse(errorRespCpy);
    this.cdr.detectChanges();
  }

  addRow() {
    if (this.rows.length < 20) {
      this.rows.push(this.createRow());
    }
  }

  removeRow(index: number) {
    this.rows.removeAt(index);
  }

  onSubmit() {
    this.isSubmitLoading.set(true);

    if (this.dynamicForm.invalid) {
      this.formService.markAllAsTouched(this.dynamicForm);
      this.isSubmitLoading.set(false);
      return;
    }

    this.saveFormDataToCache();
    this.navigationService.changeScreenTo(ScreenType.OPCJE_DORECZENIA);
    this.isSubmitLoading.set(false);
  }

  backButtonAction() {
    this.navigationService.changeScreenTo(ScreenType.STRONA_GLOWNA);
  }
}
